import { useCallback, useState, useRef } from 'react';
import { INITIAL_COMPONENTS } from '../config/componentLibrary.js';
import { generateId, measureNode, deepCloneNodes } from '../utils.js';
import { resolveNode } from '../utils/responsive.js';

export const useDragAndDrop = ({
  canvasRef,
  canvasScale,
  prefabs,
  rootW,
  rootH,
  currentPrefabId,
  activeNodes,
  selectedNodeIds,
  historyExecute,
  containerNode,
  activeBreakpoint = 'default',
  onNodeAdded
}) => {
  const [dragPreview, setDragPreview] = useState(null);
  const [draggedPrefabId, setDraggedPrefabId] = useState(null);
  const [previewNodesBase, setPreviewNodesBase] = useState(null);
  const rafRef = useRef(null);
  const mousePos = useRef({ x: 0, y: 0 });
  const dragCounter = useRef(0);

  const handleDrop = useCallback((e) => {
    dragCounter.current = 0;
    setDragPreview(null);
    setDraggedPrefabId(null);
    setPreviewNodesBase(null);
    e.preventDefault();

    const libraryLabel = e.dataTransfer.getData('libraryLabel');
    const librarySection = e.dataTransfer.getData('librarySection') || 'Primitives';
    const prefabId = e.dataTransfer.getData('prefabId');

    if (!libraryLabel && !prefabId) return;
    if (prefabId && currentPrefabId && prefabId === currentPrefabId) return;
    if (!canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left) / canvasScale;
    const mouseY = (e.clientY - rect.top) / canvasScale;

    // Determine Parent and Local Coordinates
    let parentId = undefined;
    let localX = mouseX;
    let localY = mouseY;

    // If isolated in a standard container, parent new items to it
    if (containerNode && activeNodes.some(n => n.id === containerNode.id)) {
        parentId = containerNode.id;
        const containerRect = measureNode(containerNode.id, activeNodes, rootW, rootH);
        localX -= containerRect.x;
        localY -= containerRect.y;
    } 
    // If in Prefab Context (swapped activeNodes), parentId remains undefined (Root of Definition)
    // and coordinates are local to definition (which maps to Canvas 0,0 usually)
    // Since we are not doing In-Place Prefab Editing yet (just Standard Isolation), this default is fine.

    let newNodes = [];

    if (prefabId) {
      const def = prefabs.find(p => p.id === prefabId);
      if (def && def.nodes && def.nodes.length > 0) {
        // Find the root node (node without parentId)
        const defRoot = def.nodes.find(n => !n.parentId);
        if (!defRoot) return;

        // Clone all nodes from the prefab definition
        const cloneResult = deepCloneNodes(def.nodes, defRoot.id, true);
        if (cloneResult) {
          const { newNodes: clonedNodes, newRootId } = cloneResult;
          const clonedRoot = clonedNodes.find(n => n.id === newRootId);

          if (clonedRoot) {
            const w = def.defaultWidth ?? (clonedRoot.width || 100);
            const h = def.defaultHeight ?? (clonedRoot.height || 100);

            // Force anchors to Top-Left to ensure absolute positioning matches drop coordinates
            clonedRoot.anchors = { minX: 0, maxX: 0, minY: 0, maxY: 0 };

            // Resolve the current visual position (could be from an override)
            // Use this as the "Old X/Y" to calculate Delta
            const resolvedRoot = resolveNode(clonedRoot, activeBreakpoint);
            const visualOldX = resolvedRoot.x;
            const visualOldY = resolvedRoot.y;

            const newX = Math.round(localX - w / 2);
            const newY = Math.round(localY - h / 2);
            
            // Calculate delta based on VISUAL difference
            const deltaX = newX - visualOldX;
            const deltaY = newY - visualOldY;

            // Apply delta to BASE position
            clonedRoot.x = Math.round(clonedRoot.x + deltaX);
            clonedRoot.y = Math.round(clonedRoot.y + deltaY);
            
            clonedRoot.width = Math.round(w);
            clonedRoot.height = Math.round(h);
            clonedRoot.parentId = parentId;

            // Update responsive overrides to match new position by applying SAME delta
            if (clonedRoot.props?.responsiveOverrides) {
              const updatedOverrides = {};
              Object.keys(clonedRoot.props.responsiveOverrides).forEach(bp => {
                const bpOverrides = { ...clonedRoot.props.responsiveOverrides[bp] };
                // Update layout properties if they exist
                if (bpOverrides.layout_x !== undefined) {
                  bpOverrides.layout_x = Math.round(bpOverrides.layout_x + deltaX);
                }
                if (bpOverrides.layout_y !== undefined) {
                  bpOverrides.layout_y = Math.round(bpOverrides.layout_y + deltaY);
                }
                // Strip layout anchors from overrides to enforce absolute positioning
                if (bpOverrides.layout_anchors) {
                    delete bpOverrides.layout_anchors;
                }
                updatedOverrides[bp] = bpOverrides;
              });
              clonedRoot.props = {
                ...clonedRoot.props,
                responsiveOverrides: updatedOverrides,
                prefabId: prefabId
              };
            } else {
              clonedRoot.props = { ...clonedRoot.props, prefabId: prefabId };
            }
            clonedRoot.label = def.name;

            // Children already have relative coordinates from the definition, no adjustment needed
            newNodes = clonedNodes;
          }
        }
      }
    } else {
      const preset = INITIAL_COMPONENTS.find(c => c.label === libraryLabel && (c.section || 'Primitives') === librarySection)
        || INITIAL_COMPONENTS.find(c => c.label === libraryLabel);
      if (preset) {
        const newComps = (preset.defaultComponents || []).map(dc => ({
          id: generateId(dc.type),
          type: dc.type,
          props: { ...dc.props }
        }));
        const w = preset.defaultSize.w;
        const h = preset.defaultSize.h;
        const newNode = {
          id: `Box-${Date.now()}`,
          type: 'Box',
          x: localX - w / 2,
          y: localY - h / 2,
          width: w,
          height: h,
          anchors: { minX: 0, maxX: 0, minY: 0, maxY: 0 },
          props: { ...preset.defaultProps },
          attachedComponents: newComps,
          label: preset.label,
          parentId
        };
        newNodes = [newNode];
      }
    }

    if (newNodes.length > 0) {
      const rootNode = newNodes.find(n => !n.parentId || n.parentId === parentId);
      historyExecute(activeNodes, [...activeNodes, ...newNodes], selectedNodeIds, rootNode ? [rootNode.id] : [newNodes[0].id]);
      if (onNodeAdded) onNodeAdded(newNodes);
    }
  }, [
    canvasRef,
    canvasScale,
    prefabs,
    rootW,
    rootH,
    currentPrefabId,
    activeNodes,
    selectedNodeIds,
    historyExecute,
    containerNode,
    onNodeAdded
  ]);

  const handleDragStart = useCallback((prefabId) => {
    setDraggedPrefabId(prefabId);

    // Clone the prefab nodes once at the start
    const def = prefabs?.find(p => p.id === prefabId);
    if (def && def.nodes && def.nodes.length > 0) {
      const defRoot = def.nodes.find(n => !n.parentId);
      if (defRoot) {
        const cloneResult = deepCloneNodes(def.nodes, defRoot.id);
        if (cloneResult) {
          const { newNodes: clonedNodes, newRootId } = cloneResult;

          // Force root to Top-Left anchors for dragging to ensure x/y map directly to canvas pixels
          const rootNode = clonedNodes.find(n => n.id === newRootId);
          if (rootNode) {
              rootNode.anchors = { minX: 0, maxX: 0, minY: 0, maxY: 0 };
              // Ensure width/height are set to what we use for centering
              rootNode.width = def.defaultWidth ?? (rootNode.width || 100);
              rootNode.height = def.defaultHeight ?? (rootNode.height || 100);
          }

          // Strip responsive positioning overrides ONLY from the root node to prevent position fighting
          // But keep styling overrides (colors, etc) and keep all child overrides
          const previewNodes = clonedNodes.map(n => {
            if (n.id === newRootId && n.props && n.props.responsiveOverrides) {
                const newOverrides = {};
                Object.keys(n.props.responsiveOverrides).forEach(bp => {
                    // Extract layout positioning props we want to remove
                    const { layout_x, layout_y, layout_anchors, ...rest } = n.props.responsiveOverrides[bp];
                    newOverrides[bp] = rest;
                });
                return {
                    ...n,
                    props: {
                        ...n.props,
                        responsiveOverrides: newOverrides
                    }
                };
            }
            return n;
          });

          setPreviewNodesBase({
            nodes: previewNodes,
            rootId: newRootId,
            width: def.defaultWidth ?? (defRoot.width || 100),
            height: def.defaultHeight ?? (defRoot.height || 100),
            prefabId
          });
        }
      }
    }
  }, [prefabs]);

  const updatePreview = useCallback(() => {
    if (!previewNodesBase || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = (mousePos.current.x - rect.left) / canvasScale;
    const y = (mousePos.current.y - rect.top) / canvasScale;

    const previewX = x - previewNodesBase.width / 2;
    const previewY = y - previewNodesBase.height / 2;

    const clonedRoot = previewNodesBase.nodes.find(n => n.id === previewNodesBase.rootId);
    if (clonedRoot) {
      const deltaX = previewX - clonedRoot.x;
      const deltaY = previewY - clonedRoot.y;

      const previewNodes = previewNodesBase.nodes.map(n => {
        if (n.id === previewNodesBase.rootId) {
            return {
                ...n,
                x: n.x + deltaX,
                y: n.y + deltaY
            };
        }
        return n;
      });

      setDragPreview({
        nodes: previewNodes,
        rootId: previewNodesBase.rootId,
        prefabId: previewNodesBase.prefabId
      });
    }
  }, [canvasRef, canvasScale, previewNodesBase]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();

    if (previewNodesBase && canvasRef.current) {
      // Update mouse position
      mousePos.current = { x: e.clientX, y: e.clientY };

      // Cancel any pending animation frame
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      // Schedule update for next frame
      rafRef.current = requestAnimationFrame(updatePreview);
    }
  }, [previewNodesBase, updatePreview]);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    dragCounter.current += 1;
  }, []);

  const handleDragLeave = useCallback((e) => {
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      setDragPreview(null);
      // We do NOT clear previewNodesBase here, as we might re-enter
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    dragCounter.current = 0;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setDragPreview(null);
    setDraggedPrefabId(null);
    setPreviewNodesBase(null);
  }, []);

  return { handleDrop, handleDragOver, handleDragEnter, handleDragLeave, handleDragStart, handleDragEnd, dragPreview };
};
